import React, { useState, useEffect } from 'react';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from 'react-icons/fa';

// トーストの種類
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// トーストコンテキスト（グローバル状態管理）
let toastContainer = null;
let toastId = 0;

// トースト表示関数
export const showToast = (
  message,
  type = TOAST_TYPES.INFO,
  duration = 5000
) => {
  if (!toastContainer) {
    console.warn('Toast container not initialized');
    return;
  }

  const id = ++toastId;
  const toast = {
    id,
    message,
    type,
    duration,
    timestamp: Date.now(),
  };

  toastContainer.addToast(toast);
  return id;
};

// 便利な関数群
export const toast = {
  success: (message, duration) =>
    showToast(message, TOAST_TYPES.SUCCESS, duration),
  error: (message, duration) => showToast(message, TOAST_TYPES.ERROR, duration),
  warning: (message, duration) =>
    showToast(message, TOAST_TYPES.WARNING, duration),
  info: (message, duration) => showToast(message, TOAST_TYPES.INFO, duration),
};

// 個別のトーストコンポーネント
const ToastItem = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    // アニメーション開始
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 自動削除タイマー
    if (toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  const getToastStyles = () => {
    const baseClasses = `
      max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 p-4 mb-3
      transform transition-all duration-300 ease-in-out
      ${isVisible && !isRemoving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
    `;

    const typeClasses = {
      [TOAST_TYPES.SUCCESS]: 'border-green-500',
      [TOAST_TYPES.ERROR]: 'border-red-500',
      [TOAST_TYPES.WARNING]: 'border-yellow-500',
      [TOAST_TYPES.INFO]: 'border-blue-500',
    };

    return `${baseClasses} ${typeClasses[toast.type] || typeClasses[TOAST_TYPES.INFO]}`;
  };

  const getIcon = () => {
    const iconClasses = 'flex-shrink-0 w-5 h-5 mr-3';

    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return <FaCheckCircle className={`${iconClasses} text-green-500`} />;
      case TOAST_TYPES.ERROR:
        return (
          <FaExclamationTriangle className={`${iconClasses} text-red-500`} />
        );
      case TOAST_TYPES.WARNING:
        return (
          <FaExclamationTriangle className={`${iconClasses} text-yellow-500`} />
        );
      case TOAST_TYPES.INFO:
        return <FaInfoCircle className={`${iconClasses} text-blue-500`} />;
      default:
        return <FaInfoCircle className={`${iconClasses} text-blue-500`} />;
    }
  };

  return (
    <div className={getToastStyles()}>
      <div className="flex items-start">
        {getIcon()}
        <div className="flex-1 text-sm text-gray-700">{toast.message}</div>
        <button
          onClick={handleRemove}
          className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FaTimes className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// メインのトーストコンテナコンポーネント
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  // グローバルアクセス用の関数を設定
  useEffect(() => {
    toastContainer = {
      addToast: (toast) => {
        setToasts((prev) => [...prev, toast]);
      },
      removeToast: (id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      },
    };

    return () => {
      toastContainer = null;
    };
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed top-4 right-4 z-[10000] pointer-events-none">
      <div className="pointer-events-auto">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </div>
  );
};

export default ToastContainer;
